import {ref} from 'vue'
import {createGlobalState} from '@vueuse/core'
import {SizeTokenType} from '@toolify/client/src/modules/theme/enum/SizeTokenType'
import {TextTokenType} from '@toolify/client/src/modules/theme/enum/TextTokenType'
import {IconTokenType} from '@toolify/client/src/modules/theme/enum/IconTokenType'
import {RadiusTokenType} from '@toolify/client/src/modules/theme/enum/RadiusTokenType'
import {SpaceTokenType} from '@toolify/client/src/modules/theme/enum/SpaceTokenType'
import {WidthTokenType} from '@toolify/client/src/modules/theme/enum/WidthTokenType'

const SIZE_TOKENS: SizeTokenType[] = Object.values(SizeTokenType)

const BASE_SIZE_REM: Record<SizeTokenType, number> = {
  [SizeTokenType.Xs]: 1.25,
  [SizeTokenType.Sm]: 1.5,
  [SizeTokenType.Md]: 1.75,
  [SizeTokenType.Lg]: 2,
  [SizeTokenType.Xl]: 2.25,
  [SizeTokenType.Xl2]: 2.5,
  [SizeTokenType.Xl3]: 2.75,
  [SizeTokenType.Xl4]: 3,
  [SizeTokenType.Row]: 2.25,
  [SizeTokenType.Target]: 1.5,
  [SizeTokenType.Touch]: 2.75,
}

const FALLBACK_ICON_REM: Record<IconTokenType, number> = {
  [IconTokenType.Large]: 1,
  [IconTokenType.Standard]: 1,
  [IconTokenType.Medium]: 1,
  [IconTokenType.Small]: 0.875,
  [IconTokenType.XSmall]: 0.75,
}

const ICON_TOKENS: IconTokenType[] = Object.values(IconTokenType)

const RADIUS_TOKENS: RadiusTokenType[] = Object.values(RadiusTokenType)

const SPACE_TOKENS: SpaceTokenType[] = Object.values(SpaceTokenType)

const FALLBACK_SPACE_REM: Record<SpaceTokenType, number> = {
  [SpaceTokenType.None]: 0,
  [SpaceTokenType.Hairline]: 0.0625,
  [SpaceTokenType.Xs]: 0.125,
  [SpaceTokenType.Sm]: 0.25,
  [SpaceTokenType.SmPlus]: 0.375,
  [SpaceTokenType.Md]: 0.5,
  [SpaceTokenType.Lg]: 0.75,
  [SpaceTokenType.Xl]: 1,
  [SpaceTokenType.Xl2]: 1.25,
  [SpaceTokenType.Xl3]: 1.5,
  [SpaceTokenType.Xl4]: 2,
  [SpaceTokenType.Xl5]: 2.25,
}

const WIDTH_TOKENS: WidthTokenType[] = Object.values(WidthTokenType)

const FALLBACK_WIDTH_REM: Record<WidthTokenType, number> = {
  [WidthTokenType.List]: 18.75,
}

const FALLBACK_RADIUS_REM: Record<RadiusTokenType, number> = {
  [RadiusTokenType.ComponentLarge]: 0.5,
  [RadiusTokenType.ComponentStandard]: 0.5,
  [RadiusTokenType.ComponentMedium]: 0.5,
  [RadiusTokenType.ComponentSmall]: 0.5,
  [RadiusTokenType.Xs]: 0.125,
  [RadiusTokenType.Xl2]: 1,
  [RadiusTokenType.Full]: 62.4375,
}

function parseScaleRem(raw: string, fallback: number): number {
  const numbers = raw.match(/[0-9]*\.?[0-9]+/g)
  if(!numbers?.length) {
    return fallback
  }
  const product = numbers.reduce((acc, value) => acc * Number.parseFloat(value), 1)
  return Number.isFinite(product) && product > 0 ? product : fallback
}

const SIZE_TOKEN_BY_BASE_REM = new Map<number, SizeTokenType>(
  SIZE_TOKENS
    .filter(token => token !== SizeTokenType.Touch
      && token !== SizeTokenType.Target
      && token !== SizeTokenType.Row)
    .map(token => [BASE_SIZE_REM[token], token]),
)

const TEXT_REM: Record<TextTokenType, number> = {
  [TextTokenType.Xs3]: 0.5,
  [TextTokenType.Xs2]: 0.625,
  [TextTokenType.Xs]: 0.75,
  [TextTokenType.Sm]: 0.875,
  [TextTokenType.Base]: 1,
  [TextTokenType.Lg]: 1.125,
  [TextTokenType.Xl]: 1.25,
  [TextTokenType.Xl2]: 1.5,
  [TextTokenType.Xl3]: 1.875,
  [TextTokenType.Xl4]: 2.25,
  [TextTokenType.Xl5]: 3,
  [TextTokenType.Xl6]: 3.75,
  [TextTokenType.Xl7]: 4.5,
}

const useRootStyleState = createGlobalState(() => {
  const htmlFontSize = ref(16)
  const fontScale = ref(1)

  const sizeScaleRem = ref<Record<SizeTokenType, number>>({...BASE_SIZE_REM})
  const iconScaleRem = ref<Record<IconTokenType, number>>({...FALLBACK_ICON_REM})
  const radiusScaleRem = ref<Record<RadiusTokenType, number>>({...FALLBACK_RADIUS_REM})
  const spaceScaleRem = ref<Record<SpaceTokenType, number>>({...FALLBACK_SPACE_REM})
  const widthScaleRem = ref<Record<WidthTokenType, number>>({...FALLBACK_WIDTH_REM})

  function readHtmlFontSize(): number {
    return parseFloat(
      getComputedStyle(document.documentElement).fontSize || '16',
    )
  }

  function readFontScale(): number {
    const raw = getComputedStyle(document.documentElement).getPropertyValue('--font-scale').trim()
    const parsed = parseFloat(raw)
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1
  }

  function readSizeScaleRem(rootFontSize: number): Record<SizeTokenType, number> {
    const styles = getComputedStyle(document.documentElement)
    const next = {} as Record<SizeTokenType, number>
    for(const token of SIZE_TOKENS) {
      const raw = styles.getPropertyValue(`--${token}`).trim()
      const parsed = parseFloat(raw)
      if(!Number.isFinite(parsed) || parsed <= 0) {
        next[token] = BASE_SIZE_REM[token]
        continue
      }
      next[token] = raw.endsWith('px') ? parsed / rootFontSize : parsed
    }
    return next
  }

  function resolveSizeRem(token: SizeTokenType): number {
    return sizeScaleRem.value[token]
  }

  function readIconScaleRem(): Record<IconTokenType, number> {
    const styles = getComputedStyle(document.documentElement)
    const next = {} as Record<IconTokenType, number>
    for(const token of ICON_TOKENS) {
      next[token] = parseScaleRem(
        styles.getPropertyValue(`--${token}`).trim(),
        FALLBACK_ICON_REM[token],
      )
    }
    return next
  }

  function readRadiusScaleRem(rootFontSize: number): Record<RadiusTokenType, number> {
    const styles = getComputedStyle(document.documentElement)
    const next = {} as Record<RadiusTokenType, number>
    for(const token of RADIUS_TOKENS) {
      const raw = styles.getPropertyValue(`--${token}`).trim()
      const value = parseScaleRem(raw, FALLBACK_RADIUS_REM[token])
      next[token] = raw.endsWith('px') ? value / rootFontSize : value
    }
    return next
  }

  function readLengthScaleRem<T extends string>(
    tokens: T[],
    fallbackRem: Record<T, number>,
    rootFontSize: number,
  ): Record<T, number> {
    const styles = getComputedStyle(document.documentElement)
    const next = {} as Record<T, number>
    for(const token of tokens) {
      const raw = styles.getPropertyValue(`--${token}`).trim()
      if(!raw) {
        next[token] = fallbackRem[token]
        continue
      }
      const parsed = Number.parseFloat(raw)
      if(!Number.isFinite(parsed)) {
        next[token] = fallbackRem[token]
        continue
      }
      next[token] = raw.endsWith('px') ? parsed / rootFontSize : parsed
    }
    return next
  }

  function refresh() {
    const fontSize = readHtmlFontSize()
    if(fontSize && fontSize !== htmlFontSize.value) {
      htmlFontSize.value = fontSize
    }
    const scale = readFontScale()
    if(scale !== fontScale.value) {
      fontScale.value = scale
    }
    const nextSizes = readSizeScaleRem(htmlFontSize.value)
    const currentSizes = sizeScaleRem.value
    if(SIZE_TOKENS.some(token => nextSizes[token] !== currentSizes[token])) {
      sizeScaleRem.value = nextSizes
    }
    const nextIcons = readIconScaleRem()
    const currentIcons = iconScaleRem.value
    if(ICON_TOKENS.some(token => nextIcons[token] !== currentIcons[token])) {
      iconScaleRem.value = nextIcons
    }
    const nextRadiuses = readRadiusScaleRem(htmlFontSize.value)
    const currentRadiuses = radiusScaleRem.value
    if(RADIUS_TOKENS.some(token => nextRadiuses[token] !== currentRadiuses[token])) {
      radiusScaleRem.value = nextRadiuses
    }
    const nextSpaces = readLengthScaleRem(SPACE_TOKENS, FALLBACK_SPACE_REM, htmlFontSize.value)
    const currentSpaces = spaceScaleRem.value
    if(SPACE_TOKENS.some(token => nextSpaces[token] !== currentSpaces[token])) {
      spaceScaleRem.value = nextSpaces
    }
    const nextWidths = readLengthScaleRem(WIDTH_TOKENS, FALLBACK_WIDTH_REM, htmlFontSize.value)
    const currentWidths = widthScaleRem.value
    if(WIDTH_TOKENS.some(token => nextWidths[token] !== currentWidths[token])) {
      widthScaleRem.value = nextWidths
    }
  }

  refresh()
  const observer = new MutationObserver(refresh)
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['data-theme', 'style', 'class'],
  })

  return {
    htmlFontSize,
    fontScale,
    iconScaleRem,
    radiusScaleRem,
    spaceScaleRem,
    widthScaleRem,
    resolveSizeRem,
  }
})

export function startRootStyleObserver() {
  useRootStyleState()
}

export function useRem() {
  const {htmlFontSize, fontScale, resolveSizeRem} = useRootStyleState()

  function remToPx(rem: number): number {
    return rem * htmlFontSize.value
  }

  function pxToRem(px: number): number {
    return px / htmlFontSize.value
  }

  function remToFontPx(rem: number): number {
    return rem * htmlFontSize.value * fontScale.value
  }

  function remToSizePx(rem: number): number {
    const token = SIZE_TOKEN_BY_BASE_REM.get(rem)
    return token ? resolveSizeRem(token) * htmlFontSize.value : remToPx(rem)
  }

  return {
    htmlFontSize,
    fontScale,
    remToPx,
    pxToRem,
    remToFontPx,
    remToSizePx,
  }
}

export function useSizeScale() {
  const {htmlFontSize, fontScale, iconScaleRem, radiusScaleRem, spaceScaleRem, widthScaleRem, resolveSizeRem} = useRootStyleState()

  function sizeRem(token: SizeTokenType): number {
    return resolveSizeRem(token)
  }

  function sizePx(token: SizeTokenType): number {
    return resolveSizeRem(token) * htmlFontSize.value
  }

  function iconPx(token: IconTokenType): number {
    return iconScaleRem.value[token] * htmlFontSize.value
  }

  function radiusPx(token: RadiusTokenType): number {
    return radiusScaleRem.value[token] * htmlFontSize.value
  }

  function spacePx(token: SpaceTokenType): number {
    return spaceScaleRem.value[token] * htmlFontSize.value
  }

  function widthPx(token: WidthTokenType): number {
    return widthScaleRem.value[token] * htmlFontSize.value
  }

  function textPx(token: TextTokenType): number {
    return TEXT_REM[token] * htmlFontSize.value * fontScale.value
  }

  return {
    sizeRem,
    sizePx,
    iconPx,
    radiusPx,
    spacePx,
    widthPx,
    textPx,
  }
}
